import React, { useEffect, useMemo, useState, useCallback } from "react";
import clsx from "clsx";

import AchievementRate from "../../../organisms/student/ActionList/AchievementRate";
import VolumeList from "../../../organisms/student/ActionList/VolumeList";
import ActionAnswerForm from "../../../organisms/student/ActionList/ActionAnswerForm";
import { IActionList, IActionLessons } from "../../../state";
import { ReactComponent as ActionListIcon } from "../../../assets/images/ActionList/icon.svg";

import styles from "./ActionList.module.scss";
export interface IActionListProps {
  actionList: IActionList;
  handleSubmitRealization: (
    lesson_id: number,
    content: string,
    realization_id: number | null,
  ) => void;
  calling: boolean;
}
const ActionList: React.FC<IActionListProps> = ({
  actionList,
  handleSubmitRealization,
  calling,
}) => {
  const [activeVolumeId, setActiveVolumeId] = useState<number | null>(null);

  // データ上は回答が存在しても内容が空文字の場合は未回答として扱う
  const isRetrospectiveAnswered = (lesson: IActionLessons) =>
    lesson.answer?.contents?.[1]?.content !== undefined &&
    lesson.answer?.contents?.[0]?.content !== "" &&
    lesson.answer?.contents?.[1]?.content !== "";

  const { achieved, total } = useMemo(() => {
    const totalAnsweredLessons = actionList.teaching_materials.reduce(
      (count, material) => {
        const lessonsWithAnswerCount =
          material.lessons?.filter(lesson => isRetrospectiveAnswered(lesson))
            .length || 0;
        return count + lessonsWithAnswerCount;
      },
      0,
    );

    const achievedLessonsCount = actionList.teaching_materials.reduce(
      (total, material) =>
        total +
        material.lessons!.filter(
          lesson => lesson.reflection_realization_reference !== null,
        ).length,
      0,
    );

    return { achieved: achievedLessonsCount, total: totalAnsweredLessons };
  }, [actionList]);

  const volumeList = actionList.teaching_materials;

  const checkAnswerContent = useCallback(volume => {
    if (!volume.lessons || volume.lessons.length === 0) {
      return false;
    }

    return volume.lessons.some((lesson: IActionLessons) => {
      return isRetrospectiveAnswered(lesson);
    });
  }, []);

  // 両方の条件を満たす場合にのみ有効（disabled状態でない）と判定する
  const hasAnsweredLessons = useCallback(
    volume =>
      volume.lessons && volume.lessons.length > 0 && checkAnswerContent(volume),
    [checkAnswerContent],
  );

  const areAllVolumesUnanswered = useMemo(() => {
    return volumeList.every(volume => !hasAnsweredLessons(volume));
  }, [volumeList, hasAnsweredLessons]);

  const selectedVolume = useMemo(() => {
    return actionList.teaching_materials.find(
      material => material.id === activeVolumeId,
    );
  }, [actionList, activeVolumeId]);

  useEffect(() => {
    if (volumeList.length > 0) {
      const firstVolumeWithAnsweredLessons = volumeList.find(volume =>
        hasAnsweredLessons(volume),
      );

      if (firstVolumeWithAnsweredLessons && activeVolumeId === null) {
        setActiveVolumeId(firstVolumeWithAnsweredLessons.id);
      }
    }
  }, [volumeList, activeVolumeId, hasAnsweredLessons]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.volumeMenu}>
        <AchievementRate achieved={achieved} total={total} />
        <div className={styles.volumeListWrapper}>
          <VolumeList
            activeVolumeId={activeVolumeId}
            setActiveVolumeId={setActiveVolumeId}
            volumeList={volumeList}
            hasAnsweredLessons={hasAnsweredLessons}
          />
        </div>
      </div>
      {areAllVolumesUnanswered ? (
        <div className={styles.message}>
          <p className={styles.text}>
            この画面には、あなたが授業中に入力した行動宣言が表示されます。
            <br />
            授業で学んだ考え方を、日常生活で使ってみましょう。
          </p>
        </div>
      ) : (
        <div className={styles.container}>
          <div className={styles.header}>
            <p className={styles.title}>
              <ActionListIcon />
              行動リスト
            </p>
            <p className={styles.text}>
              振り返りWORKに入力した内容を実践して、気づいたことや思ったことを記録しよう。
            </p>
          </div>
          <div className={styles.list}>
            {selectedVolume?.lessons?.map(lesson => {
              const isReflected =
                lesson?.reflection_realization_reference !== null;
              return (
                <div
                  className={clsx(
                    styles.item,
                    isReflected && styles.isReflected,
                  )}
                  key={lesson.id}
                >
                  <ActionAnswerForm
                    lesson={lesson}
                    handleSubmitRealization={handleSubmitRealization}
                    calling={calling}
                    isRetrospectiveAnswered={isRetrospectiveAnswered}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ActionList;
